import React from 'react';

interface TrackingProgressMapProps {
  currentStatus: string;
  shippingMethod?: string;
  orderDate: string;
  carrier?: string;
}

const statusSteps = [
  { key: 'pending', label: 'Commande reçue', icon: 'ri-checkbox-circle-line', pct: 0 },
  { key: 'processing', label: 'En préparation', icon: 'ri-settings-3-line', pct: 25 },
  { key: 'shipped', label: 'Expédiée', icon: 'ri-truck-line', pct: 55 },
  { key: 'delivered', label: 'Livrée', icon: 'ri-home-smile-line', pct: 100 },
];

export default function TrackingProgressMap({
  currentStatus,
  shippingMethod = 'standard',
  orderDate,
  carrier,
}: TrackingProgressMapProps) {
  // -------------------------------------------------------------------------
  // Helper utilities – keep them pure and defensive
  // -------------------------------------------------------------------------
  const currentIdx = statusSteps.findIndex((s) => s.key === currentStatus);
  const progress = currentIdx >= 0 ? statusSteps[currentIdx].pct : 0;
  const isCancelled = currentStatus === 'cancelled';

  const getMethodLabel = (): string => {
    switch (shippingMethod) {
      case 'express':
        return 'Express';
      case 'overnight':
        return 'Overnight';
      default:
        return 'Standard';
    }
  };

  const getEstimatedDays = (): string => {
    switch (shippingMethod) {
      case 'overnight':
        return '1 jour';
      case 'express':
        return '2-4 jours';
      default:
        return '5-8 jours';
    }
  };

  const formatOrderDate = (): string => {
    try {
      const date = new Date(orderDate);
      // If the date is invalid, fallback to a placeholder
      if (Number.isNaN(date.getTime())) throw new Error('Invalid date');
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
      });
    } catch {
      return 'Date inconnue';
    }
  };

  // -------------------------------------------------------------------------
  // Render cancelled state early
  // -------------------------------------------------------------------------
  if (isCancelled) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
            <i className="ri-close-circle-line text-lg text-red-500"></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 tracking-wider">
              PROGRESSION
            </h3>
            <p className="text-[11px] text-red-400 mt-0.5">Commande annulée</p>
          </div>
        </div>
        <div className="bg-red-50 rounded-xl p-5 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-close-circle-line text-3xl text-red-400"></i>
          </div>
          <p className="text-sm font-semibold text-red-700">
            Cette commande a été annulée
          </p>
          <p className="text-xs text-red-400 mt-1">
            Contactez notre support si vous avez des questions
          </p>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Normal progress UI
  // -------------------------------------------------------------------------
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#E8F5E9] flex items-center justify-center">
            <i className="ri-route-line text-lg text-[#2E7D32]"></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 tracking-wider">
              PROGRESSION
            </h3>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Livraison {getMethodLabel()} · {getEstimatedDays()}
            </p>
          </div>
        </div>
        <div className="text-right">
          <span className="text-2xl font-bold text-[#2E7D32]">{progress}%</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative mb-8">
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-1000 ease-out"
            style={{
              width: `${progress}%`,
              background:
                'linear-gradient(90deg, #2E7D32, #4CAF50, #81C784)',
            }}
          ></div>
        </div>

        {/* Truck icon on progress */}
        {currentStatus !== 'delivered' && (
          <div
            className="absolute top-1/2 -translate-y-1/2 transition-all duration-1000 ease-out"
            style={{ left: `calc(${Math.min(progress, 95)}% - 14px)` }}
          >
            <div
              className="w-7 h-7 rounded-full bg-[#2E7D32] flex items-center justify-center shadow-lg shadow-[#2E7D32]/30 animate-bounce"
              style={{ animationDuration: '2s' }}
            >
              <i className="ri-truck-line text-xs text-white"></i>
            </div>
          </div>
        )}

        {/* Check icon when delivered */}
        {currentStatus === 'delivered' && (
          <div className="absolute right-0 top-1/2 -translate-y-1/2">
            <div className="w-7 h-7 rounded-full bg-[#2E7D32] flex items-center justify-center shadow-lg shadow-[#2E7D32]/30">
              <i className="ri-check-double-line text-xs text-white"></i>
            </div>
          </div>
        )}
      </div>

      {/* Steps */}
      <div className="flex items-start justify-between">
        {statusSteps.map((step, idx) => {
          const isDone = idx <= currentIdx;
          const isCurrent = idx === currentIdx;
          return (
            <div
              key={step.key}
              className="flex flex-col items-center text-center flex-1"
            >
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-all ${
                  isDone
                    ? isCurrent
                      ? 'bg-[#2E7D32] text-white shadow-lg shadow-[#2E7D32]/20 ring-4 ring-[#2E7D32]/10'
                      : 'bg-[#2E7D32] text-white'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                {isDone && !isCurrent ? (
                  <i className="ri-check-line text-lg"></i>
                ) : (
                  <i className={`${step.icon} text-lg`}></i>
                )}
              </div>
              <p
                className={`text-[11px] font-semibold ${
                  isDone ? 'text-[#2E7D32]' : 'text-gray-400'
                }`}
              >
                {step.label}
              </p>
            </div>
          );
        })}
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-3 gap-3 mt-6 pt-5 border-t border-gray-100">
        <div className="bg-[#FAFDF7] rounded-lg p-3 text-center">
          <i className="ri-calendar-line text-base text-[#2E7D32] mb-1 block w-5 h-5 flex items-center justify-center mx-auto"></i>
          <p className="text-[10px] text-gray-400 mb-0.5">Commandé le</p>
          <p className="text-xs font-semibold text-gray-800">
            {formatOrderDate()}
          </p>
        </div>
        <div className="bg-[#FAFDF7] rounded-lg p-3 text-center">
          <i className="ri-truck-line text-base text-[#2E7D32] mb-1 block w-5 h-5 flex items-center justify-center mx-auto"></i>
          <p className="text-[10px] text-gray-400 mb-0.5">Transporteur</p>
          <p className="text-xs font-semibold text-gray-800 uppercase">
            {carrier || 'En attente'}
          </p>
        </div>
        <div className="bg-[#FAFDF7] rounded-lg p-3 text-center">
          <i className="ri-time-line text-base text-[#2E7D32] mb-1 block w-5 h-5 flex items-center justify-center mx-auto"></i>
          <p className="text-[10px] text-gray-400 mb-0.5">Délai estimé</p>
          <p className="text-xs font-semibold text-gray-800">
            {getEstimatedDays()}
          </p>
        </div>
      </div>
    </div>
  );
}
